import { computed, nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useQuery } from "@vue/apollo-composable";

import GetRecentProjectsQuery from "@/graphql/queries/GetRecentProjectsQuery";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import useHasuraStore from "@/stores/hasura/hasura.store";

// TODO: after `npm run codegen` runs against a schema that includes the
// `user_project_view` table, swap this for `useGetRecentProjectsQuery` from
// `@/graphql` so we get full type safety on the result shape.

type RecentProjectRow = {
  projectId: string;
  viewedAt: string;
  project: {
    id: string;
    name: string;
    projectStatus: { id: number } | null;
  } | null;
};

type GetRecentProjectsResult = {
  userProjectView: RecentProjectRow[];
};

export type RecentProject = { id: string; name: string; viewedAt: string; isActive: boolean };

// Project statuses considered "not active": Archived (5) and On Hold (6).
const INACTIVE_PROJECT_STATUS_IDS = [5, 6];

// Shared rule for the green/yellow status dot: a project is "active" unless its
// status is Archived or On Hold. Used by recents and the project switcher search.
export const isActiveProjectStatus = (statusId: number | null | undefined): boolean =>
  !INACTIVE_PROJECT_STATUS_IDS.includes(statusId ?? -1);

const EMPTY_RECENTS = computed(() => [] as RecentProject[]);

export const useRecentProjects = (limit = 5) => {
  // The recents sidebar is a non-essential surface — JWT failures, schema
  // mismatches, or Apollo setup errors must never break the parent layout.
  // We fall back to an empty list instead of letting the error propagate.
  try {
    // Scope recents to the current org by passing tenantId into the where clause
    // (filtering on the joined project's tenantId, not the view row's tenant_id).
    // A privileged user (accessAllProjects) can view cross-tenant projects from
    // one session, producing view rows whose tenant matches the session but
    // whose project belongs to another org — server-side filtering keeps those
    // out of this org's recents.
    const authState = useAuthStateStore();
    const { hasuraToken } = storeToRefs(useHasuraStore());

    // The Hasura JWT is fetched *separately* from the Frontegg user update and
    // lands a tick later (see hasura.store). On an org switch, user.tenantId
    // changes the moment Frontegg confirms it, but the matching token arrives
    // afterwards. If the query auto-fires on that variable change it goes out
    // with the PREVIOUS org's token; Hasura's RLS then returns an empty list,
    // and that stale response can land *after* the correct refetch and clobber
    // it — so recents stay empty until a full reload. To avoid that we pause
    // the query whenever the tenant changes and only resume once the token for
    // the new tenant has settled. Initialise from the current token so the very
    // first load (token already present) is not left permanently paused.
    const isTokenSettledForTenant = ref(!!hasuraToken.value);

    const { result, loading, refetch } = useQuery<GetRecentProjectsResult, { limit: number; tenantId: string }>(
      GetRecentProjectsQuery,
      () => ({ limit, tenantId: authState.user?.tenantId ?? "" }),
      () => ({
        enabled: !!authState.user?.tenantId && authState.isTenantIdValid && isTokenSettledForTenant.value,
        fetchPolicy: "cache-and-network",
        errorPolicy: "ignore",
        notifyOnNetworkStatusChange: false,
      })
    );

    // Tenant changed → the current token is now stale for this tenant; pause the
    // query until the new token lands so it never fires with the old JWT.
    watch(
      () => authState.user?.tenantId,
      () => {
        isTokenSettledForTenant.value = false;
      }
    );

    // Token for the (new) tenant has settled (login, tenant switch, re-auth) →
    // resume the query and refetch so the request carries the correct JWT.
    // nextTick lets hasura.store's watcher push the new token into Apollo's
    // defaultContext before our refetch builds the request.
    watch(hasuraToken, async () => {
      await nextTick();
      isTokenSettledForTenant.value = true;
      refetch();
    });

    // Hasura already filters by tenant + non-deleted via the where clause, so
    // we only need a TypeScript null-check on the optional project relationship.
    const recents = computed<RecentProject[]>(() =>
      (result.value?.userProjectView ?? [])
        .filter((row): row is RecentProjectRow & { project: NonNullable<RecentProjectRow["project"]> } => row.project !== null)
        .map((row) => ({
          id: row.project.id,
          name: row.project.name,
          viewedAt: row.viewedAt,
          isActive: isActiveProjectStatus(row.project.projectStatus?.id),
        }))
    );

    return { recents, loading, refetch };
  } catch {
    return { recents: EMPTY_RECENTS, loading: ref(false), refetch: () => Promise.resolve(undefined) };
  }
};
